import {useEffect, useRef} from "react";
import {useParams} from "react-router-dom";
import useGeneByIdentifier from "../../hooks/useGeneByIdentifier";
import {usePageTitle} from "../../hooks/usePageTitle";
import {AppSettings} from "../../settings";

const BASE_URL = `${window.location.origin}${AppSettings.LOCAL_URL_PREFIX}`;

const setMetaName = (name: string, content: string) => {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) { el = document.createElement('meta'); el.name = name; document.head.appendChild(el); }
  el.content = content;
};

const setMetaProp = (property: string, content: string) => {
  let el = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute('property', property); document.head.appendChild(el); }
  el.content = content;
};

const setCanonical = (href: string) => {
  const el = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (el) el.href = href;
};

interface MetaSnapshot {
  description: string;
  canonical: string;
  ogTitle: string;
  ogDescription: string;
  ogUrl: string;
}

const snapshotMeta = (): MetaSnapshot => ({
  description:   document.querySelector('meta[name="description"]')?.getAttribute('content') ?? '',
  canonical:     document.querySelector('link[rel="canonical"]')?.getAttribute('href') ?? '',
  ogTitle:       document.querySelector('meta[property="og:title"]')?.getAttribute('content') ?? '',
  ogDescription: document.querySelector('meta[property="og:description"]')?.getAttribute('content') ?? '',
  ogUrl:         document.querySelector('meta[property="og:url"]')?.getAttribute('content') ?? '',
});

const applyMeta = (snap: MetaSnapshot) => {
  setMetaName('description', snap.description);
  setCanonical(snap.canonical);
  setMetaProp('og:title', snap.ogTitle);
  setMetaProp('og:description', snap.ogDescription);
  setMetaProp('og:url', snap.ogUrl);
};

const GenePageTitleController = () => {
  const {identifier} = useParams<{ identifier: string }>();
  const {gene} = useGeneByIdentifier(identifier);

  // Use resolved canonical symbol once available, fall back to raw identifier immediately
  const symbol = gene?.s ?? identifier;

  usePageTitle(symbol ? `${symbol} - Gene` : "Gene");

  // Capture site-level defaults on mount; restore on unmount
  const defaultsRef = useRef<MetaSnapshot | null>(null);
  useEffect(() => {
    defaultsRef.current = snapshotMeta();
    return () => { if (defaultsRef.current) applyMeta(defaultsRef.current); };
  }, []);

  // Update meta whenever symbol resolves or changes
  useEffect(() => {
    if (!symbol) return;
    const url = `${BASE_URL}/gene/${symbol}`;
    const description = `Explore genetic interactions, expression data, and fitness phenotypes for ${symbol} in the Human Cell Map genome-scale genetic interaction network.`;
    setMetaName('description', description);
    setCanonical(url);
    setMetaProp('og:title', `${symbol} – Gene | Human Cell Map`);
    setMetaProp('og:description', description);
    setMetaProp('og:url', url);
  }, [symbol]);

  return null;
};

export default GenePageTitleController;
